import { Col, FormInput, FormRadioGroup, Row } from '@snsw/react-component-library';
import { PlantItemDetailsPage } from 'plantItemNew/constants/plantItemDetailsPage';
import { RefData } from 'plantItemNew/constants/refData';
import React, { useEffect, useState } from 'react';
import { isEmpty, validateExt } from 'shared/utils/Validation';
import styles from '../PlantItemDetails.module.scss';

export const ConcretPlacingBoom = React.forwardRef((props, ref) => {
  const { value: savedPlantDetails = {}, onChange = () => {}, refData = {}, onErrorChange = () => {} } = props;

  const {
    manufacturer: savedManufacturer = '',
    serialNumber: savedSerialNumber = '',
    modelNameOrNumber: savedModelNameOrNumber = '',
    yearOfManufacture: savedYearOfManufacture = '',
    locationType: savedLocationType = '',
    specificLocation: savedSpecificLocation = '',
    uniqueIdentifier: savedUniqueIdentifier = '',
  } = savedPlantDetails;

  const itemLocationTypeOptions = Array.isArray(refData[RefData.itemLocationTypeOptions])
    ? refData[RefData.itemLocationTypeOptions].map((e) => ({
        value: e.value,
        label: e.description,
      }))
    : [];

  const [manufacturer, setManufacturer] = useState(savedManufacturer);
  const [modelNameOrNumber, setModelNameOrNumber] = useState(savedModelNameOrNumber);
  const [yearOfManufacture, setYearOfManufacture] = useState(savedYearOfManufacture);
  const [serialNumber, setSerialNumber] = useState(savedSerialNumber);
  const [uniqueIdentifier, setUniqueIdentifier] = useState(savedUniqueIdentifier);
  const [locationType, setLocationType] = useState(savedLocationType);

  const [specificLocation, setSpecificLocation] = useState(savedSpecificLocation);

  const [showErrors, setShowErrors] = useState(false);
  const [errors, setErrors] = useState({
    subType: false,
    otherSubType: false,
    manufacturer: false,
    modelNameOrNumber: false,
    yearOfManufacture: false,
    serialNumber: false,
    uniqueIdentifier: false,
    locationType: false,
    specificLocation: false,
  });

  const [errorSummary, setErrorSummary] = useState([]);

  const validators = [
    {
      fieldName: 'manufacturer',
      fieldValue: manufacturer,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please enter details',
    },
    {
      fieldName: 'modelNameOrNumber',
      fieldValue: modelNameOrNumber,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please enter details',
    },
    {
      fieldName: 'yearOfManufacture',
      fieldValue: yearOfManufacture,
      isValid: (v) => !isEmpty(v) && Number.isInteger(+v),
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter a valid year'),
    },
    {
      fieldName: 'serialNumber',
      fieldValue: serialNumber,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please enter details',
    },
    {
      fieldName: 'locationType',
      fieldValue: locationType,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
  ];

  if (ref) {
    ref.current = {
      validate: () => {
        let isCurrentComponentValid = validateExt(validators, setErrors, setErrorSummary);

        setShowErrors(!isCurrentComponentValid);

        return isCurrentComponentValid;
      },
    };
  }

  useEffect(() => {
    let itemDetails = {
      manufacturer,
      serialNumber,
      modelNameOrNumber,
      yearOfManufacture,
      locationType,
      specificLocation,
      uniqueIdentifier,
    };
    onChange(itemDetails);

    if (showErrors) validateExt(validators, setErrors, setErrorSummary);
  }, [
    manufacturer,
    modelNameOrNumber,
    yearOfManufacture,
    serialNumber,
    uniqueIdentifier,
    locationType,
    specificLocation,
  ]);

  useEffect(() => {
    onErrorChange(errorSummary);
  }, [errorSummary]);

  return (
    <>
      <Row>
        <Col span={8}>
          <FormInput
            id="manufacturer"
            label={PlantItemDetailsPage.itemDetails.manufacturerLabel}
            errorMessage={errors?.manufacturer?.errorMessage}
            hasError={showErrors && errors?.manufacturer?.invalid}
            value={manufacturer}
            onChange={(e, { value }) => setManufacturer(value)}
            inputWidth="lg"
            margin={{ top: 'md' }}
          />

          <FormInput
            label={PlantItemDetailsPage.itemDetails.modelNameOrNumberLabel}
            helpMessage="Supplier’s model name or number can be provided"
            id="modelNameOrNumber"
            errorMessage={errors?.modelNameOrNumber?.errorMessage}
            hasError={showErrors && errors?.modelNameOrNumber?.invalid}
            value={modelNameOrNumber}
            onChange={(e, { value }) => setModelNameOrNumber(value)}
            inputWidth="lg"
            margin={{ top: 'md' }}
          />

          <FormInput
            label={PlantItemDetailsPage.itemDetails.yearOfManufactureLabel}
            id="yearOfManufacture"
            errorMessage={errors?.yearOfManufacture?.errorMessage}
            helpMessage="If unknown, enter an approximate year of manufacture"
            hasError={showErrors && errors?.yearOfManufacture?.invalid}
            value={yearOfManufacture}
            onChange={(e, { value }) => setYearOfManufacture(value)}
            inputWidth="xs"
            placeholder="YYYY"
            mask={[/\d/, /\d/, /\d/, /\d/]}
            margin={{ top: 'md' }}
          />

          <div className={`${styles.labelMsg} ${styles.marginTopOneHalf}`}>
            {PlantItemDetailsPage.itemDetails.serialNumberLabel}
          </div>
          <div className={styles.helpMsg}>
            Unique identifying number issued by the manufacturer. <br />
            Note: Lifts, escalators and moving walks may have a contract number.
          </div>
          <FormInput
            id="serialNumber"
            value={serialNumber}
            onChange={(e, { value }) => setSerialNumber(value)}
            errorMessage={errors?.serialNumber?.errorMessage}
            hasError={showErrors && errors?.serialNumber?.invalid}
            inputWidth="lg"
            margin={{ top: 'none' }}
          />

          <FormRadioGroup
            id="locationType"
            name="locationType"
            legend={PlantItemDetailsPage.itemDetails.locationTypeLabel}
            vertical={true}
            options={itemLocationTypeOptions}
            onChange={setLocationType}
            value={locationType}
            errorMessage={errors?.locationType?.errorMessage}
            hasError={showErrors && errors?.locationType?.invalid}
            margin={{ top: 'md' }}
          />

          {locationType && (
            <>
              <FormInput
                label={PlantItemDetailsPage.itemDetails.specificLocationLabel}
                helpMessage="This is to help you identify where your item is specifically located at your address"
                placeholder="eg. Building name or room the item is stored in."
                value={specificLocation}
                onChange={(e, { value }) => setSpecificLocation(value)}
                margin={{ top: 'md' }}
              />

              <FormInput
                label={PlantItemDetailsPage.itemDetails.uniqueIdentifierLabel}
                helpMessage="You can provide your own unique reference number to help identify the item"
                value={uniqueIdentifier}
                onChange={(e, { value }) => setUniqueIdentifier(value)}
                inputWidth="lg"
                margin={{ top: 'md' }}
              />
            </>
          )}
        </Col>
      </Row>
    </>
  );
});

ConcretPlacingBoom.displayName = 'ConcretPlacingBoom';
